import React from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../../types';
import { toImageUrl } from '../../utils/image';
import { formatPrice, productOriginal, productSale } from '../../utils/price';

interface Props {
  product: Product;
}

const ProductCard: React.FC<Props> = ({ product }) => {
  const original = productOriginal(product, 'USD');
  const sale = productSale(product, 'USD');
  const discountRate = product.discountPercent ?? 0;

  return (
    <Link to={`/products/${product.id}`} style={styles.card}>
      <div style={styles.imageWrap}>
        <img
          src={toImageUrl(product.thumbnailUrl)}
          alt={product.name}
          style={styles.image}
        />
        {discountRate > 0 && <span style={styles.saleTag}>-{discountRate}%</span>}
      </div>
      <div style={styles.info}>
        {product.brandName && <p style={styles.brand}>{product.brandName}</p>}
        <p style={styles.name}>{product.name}</p>
        <div style={styles.priceRow}>
          {sale != null ? (
            <>
              <span style={styles.salePrice}>{formatPrice(sale, 'USD')}</span>
              <span style={styles.originalPrice}>{formatPrice(original, 'USD')}</span>
            </>
          ) : (
            <span style={styles.price}>{formatPrice(original, 'USD')}</span>
          )}
        </div>
        {(product.reviewCount ?? 0) > 0 && product.averageRating != null && (
          <p style={styles.rating}>
            <span style={styles.star}>★</span> {product.averageRating.toFixed(1)}
            <span style={styles.ratingCount}> ({product.reviewCount})</span>
          </p>
        )}
      </div>
    </Link>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    display: 'block', borderRadius: 8, overflow: 'hidden',
    background: '#fff', transition: 'box-shadow 0.2s',
    cursor: 'pointer',
  },
  imageWrap: {
    position: 'relative', paddingBottom: '100%', overflow: 'hidden',
    background: '#f5f5f5',
  },
  image: {
    position: 'absolute', top: 0, left: 0,
    width: '100%', height: '100%', objectFit: 'cover',
    transition: 'transform 0.3s',
  },
  saleTag: {
    position: 'absolute', top: 8, left: 8,
    background: '#b39ddb', color: '#fff',
    padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
  },
  soldOut: {
    position: 'absolute', inset: 0,
    background: 'rgba(0,0,0,0.4)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 16, fontWeight: 700,
  },
  info: { padding: '12px 4px' },
  brand: { fontSize: 11, color: '#999', marginBottom: 4 },
  name: { fontSize: 13, fontWeight: 500, color: '#333', marginBottom: 6, lineHeight: 1.4 },
  priceRow: { display: 'flex', alignItems: 'center', gap: 6 },
  price: { fontSize: 15, fontWeight: 700, color: '#333' },
  salePrice: { fontSize: 15, fontWeight: 700, color: '#b39ddb' },
  originalPrice: { fontSize: 12, color: '#bbb', textDecoration: 'line-through' },
  rating: { fontSize: 12, color: '#666', marginTop: 6, display: 'flex', alignItems: 'center', gap: 2 },
  star: { color: '#f5b400' },
  ratingCount: { color: '#999' },
};

export default ProductCard;
